import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import TabNavigation, { TabConfig } from '../../Components/common/TabNavigation';
import SearchIcon from '../../Images/search-icon.svg';
import DownloadIcon from '../../Images/download-icon.svg';
import ArrowUp from '../../Images/chevron-up.svg';
import ArrowDown from '../../Images/chevron-down.svg';
import ArrowDefault from '../../Images/Chevron-default-icon.svg';
import Loader from '../../Components/common/loader/loader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './PlantListTable.css';
import { getPlantsList, downloadPlantCsv } from '../../Services/serviceURL';
// Import the new useSearch hook
import useSearch from '../../hooks/useSearch'; // Adjust the path as necessary
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface PlantListData {
    id: string;
    plantId: string;
    customer: string;
    site: string;
    plant: string;
    status: string;
    isDeleted: boolean;
}

type SortableKeys = keyof PlantListData;

const PlantDataTable: React.FC = () => {
    const [activeTab, setActiveTab] = useState('Plants');
    const [sortConfig, setSortConfig] = useState<{ key: SortableKeys; direction: 'asc' | 'desc' } | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [isLoadingPlants, setIsLoadingPlants] = useState(true);
    const [allPlants, setAllPlants] = useState<PlantListData[]>([]);
    const userRole = useSelector((state: RootState) => state.auth.userRole);
    const [btnDisabled, setBtnDisabled] = useState(false);

    // Tab configuration
    const tabConfig: TabConfig[] = [
        { key: 'Plants', label: 'Plants', route: '/plants' },
        { key: 'Profile', label: 'Users', route: '/users' },
        { key: 'Securities', label: 'Securities', route: '/securities' }
    ];

    // Use the custom useSearch hook here
    // filteredPlants will now be managed by the useSearch hook
    const { searchText, filteredData: searchedPlants, handleSearchChange } = useSearch(allPlants);

    useEffect(() => {
        setIsLoadingPlants(true);
        getPlantsList()
            .then((res) => {
                const fetchedData = res.data?.Data;
                if (Array.isArray(fetchedData)) {
                    const mappedData: PlantListData[] = fetchedData.map((plant: any) => ({
                        id: plant.id || plant.plantId || Math.random().toString(36).substr(2, 9),
                        plantId: plant.plantId || plant.id || '',
                        customer: plant.customer || 'N/A',
                        site: plant.site || 'N/A',
                        plant: plant.plant || 'N/A',
                        status: plant.status || 'N/A',
                        isDeleted: plant.isDeleted || false,
                    }));
                    setAllPlants(mappedData);
                } else {
                    setAllPlants([]);
                }
            })
            .catch((err) => {
                setAllPlants([]);
            })
            .finally(() => {
                setIsLoadingPlants(false);
            });
    }, []);

    // Effect for handling page reset on search text change (now managed by useSearch hook)
    // We still need to reset currentPage when the search results change (i.e., searchedPlants changes)
    useEffect(() => {
        setCurrentPage(1);
    }, [searchedPlants]);


    const requestSort = (key: SortableKeys) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig?.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortArrow = (columnKey: SortableKeys) => {
        if (!sortConfig || sortConfig.key !== columnKey) {
            return <img className="arrow-default" src={ArrowDefault} alt="sort" />;
        }
        return sortConfig.direction === 'asc' ? (
            <img className="arrow-up" src={ArrowUp} alt="sort asc" />
        ) : (
            <img className="arrow-down" src={ArrowDown} alt="sort desc" />
        );
    };

        //new code for scroll
         useEffect(() => {
        // Reset any leftover scroll position from the page we navigated from
        // (e.g. deleting a plant from a taller Create/Edit Plant page), otherwise
        // the browser keeps the old scrollY and this shorter page can render
        // with a blank area and no way to scroll, since scrolling is locked below.
        window.scrollTo(0, 0);
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousHtmlOverflow;
        };
      }, []);
      
    const sortedAndFilteredPlants = useMemo(() => {
        const sortableItems = [...searchedPlants]; // Use searchedPlants instead of filteredPlants
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue === undefined || aValue === null) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                if (bValue === undefined || bValue === null) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }

                const valA = String(aValue).toLowerCase();
                const valB = String(bValue).toLowerCase();

                if (valA < valB) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [searchedPlants, sortConfig]); // Depend on searchedPlants

    const totalItems = sortedAndFilteredPlants.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const paginatedPlants = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedAndFilteredPlants.slice(startIndex, endIndex);
    }, [sortedAndFilteredPlants, currentPage, itemsPerPage]);

    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => {
            if (totalPages === 0) {
                return 1;
            }
            return Math.min(totalPages, prev + 1);
        });
    };

    useEffect(() => {
        if (totalPages === 0 && currentPage !== 1) {
            setCurrentPage(1);
        } else if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const navigate = useNavigate();
    const location = useLocation();

    const handleDownload = async () => {
        try {
            const response = await downloadPlantCsv();

            const contentDisposition = response.headers['content-disposition'];
            let filename = 'plants_data.csv';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\n\r"']*)['"]?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = decodeURIComponent(filenameMatch[1].replace(/\+/g, '%20'));
                }
            }

            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            a.remove();

        } catch (error) {
            // Error downloading plant CSV
        }
    };

    const handleCreatePlantClick = () => {
    
        // Clear all existing plant data when creating a new plant
        localStorage.removeItem('createPlantFormData');
        localStorage.removeItem('currentPlantData');
        localStorage.removeItem('currentPlantId');
        localStorage.removeItem('currentplantguid');
        localStorage.removeItem('customerid');
        
        navigate('/create-plant', { state: { resetForm: true } });
            
        
    };

    const handlePlantClick = (plantId: string, plantName: string) => {
        // Clear any existing plant data before navigating to a new plant
        localStorage.removeItem('createPlantFormData');
        localStorage.removeItem('currentPlantData');
        localStorage.removeItem('currentPlantId');
        localStorage.removeItem('currentplantguid');
        localStorage.removeItem('customerid');
        
        // Find the full plant data to get both ObjectId and GUID
        const plant = allPlants.find(p => p.id === plantId);
        // The GUID (plantId) is what CreatePlant needs to successfully fetch the plant
        // via getPlantById; the ObjectId alone fails that lookup (guidRegex mismatch)
        // and previously left the page with no plant/asset data after navigating back.
        const guidToUse = plant?.plantId || plantId;

        // Store the ObjectId for internal tracking
        localStorage.setItem('currentPlantId', plantId);
        // Store the GUID so CreatePlant/CreatePlantDetails can fetch plant data reliably
        localStorage.setItem('currentplantguid', guidToUse);
        
        navigate(`/create-plant/${guidToUse}`, {
            state: {
                plantName: plantName,
                breadcrumbTitle: plantName
            }
        });
    };

    const refreshPlantList = () => {
        setIsLoadingPlants(true);
        getPlantsList()
            .then((res) => {
                const fetchedData = res.data?.Data;
                if (Array.isArray(fetchedData)) {
                    const mappedData: PlantListData[] = fetchedData.map((plant: any) => ({
                        id: plant.id || plant.plantId || Math.random().toString(36).substr(2, 9),
                        plantId: plant.plantId || plant.id || '',
                        customer: plant.customer || 'N/A',
                        site: plant.site || 'N/A',
                        plant: plant.plant || 'N/A',
                        status: plant.status || 'N/A',
                        isDeleted: plant.isDeleted || false,
                    }));
                    setAllPlants(mappedData);
                } else {
                    setAllPlants([]);
                }
            })
            .catch((err) => {
                setAllPlants([]);
            })
            .finally(() => {
                setIsLoadingPlants(false);
            });
    };

    useEffect(() => {
        // Expose refreshPlantList globally for external triggers
        window.refreshPlantList = refreshPlantList;

        // Cleanup on unmount
        return () => {
            delete window.refreshPlantList;
        };
    }, []);

    useEffect(() => {
        const state = location.state as { plantDeleted?: boolean; message?: string } | null;
        if (state?.plantDeleted) {
            toast.success(state.message || 'Plant deleted successfully!');
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    return (
        <div className="plant-list-container">
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ zIndex: 10002 }}
            />
            <TabNavigation tabs={tabConfig} className="tabs-plants" />
            <div className="table-header">
                <div className="header-actions">
                    <div className="search-container-plant">
                        <img className='search-icon-lg-plant' src={SearchIcon} alt="search" />
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchText}
                            onChange={(e) => handleSearchChange(e)}
                            className="search-input-plant"
                        />
                    </div>
                </div>
                <div className="table-actions">
                    <button className="download-button-plant" onClick={handleDownload}>
                        <img className="download-icon-lg-plant" src={DownloadIcon} alt="download" />
                        Download
                    </button>
                    <button className="create-plant-button" onClick={handleCreatePlantClick} disabled={btnDisabled}>
        
                        + Create Plant
                    </button>
            
                </div>
            </div>

            <div className="table-wrapper-plant">
                <table className="global-table plant-table">
                    <thead>
                        <tr>
                            <th className="col-customer plant-col" onClick={() => requestSort('customer')}>
                                <div className="sort-header">
                                     <p className='customer-text'>Customer</p>
                                    {getSortArrow('customer')}

                                </div>
                            </th>
                            <th className="col-site" onClick={() => requestSort('site')}>
                                <div className="sort-header">
                                      <p className='customer-text' >Site</p>
                                    {getSortArrow('site')}

                                </div>
                            </th>
                            <th className="col-plant" onClick={() => requestSort('plant')}>
                                <div className="sort-header">
                                     <p className='customer-text' >Plant</p>
                                    {getSortArrow('plant')}

                                </div>
                            </th>
                            <th className="col-status" onClick={() => requestSort('status')}>
                                <div className="sort-header">
                                     <p className='customer-text' >Status</p>
                                    {getSortArrow('status')}

                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoadingPlants ? (
                            <tr>
                                <td colSpan={4} className="table-loading">
                                    <Loader />
                                </td>
                            </tr>
                        ) : paginatedPlants.length > 0 ? (
                            paginatedPlants.map((plant) => (
                                <tr key={plant.id}>
                                    <td className="text-wrap-customer-plant" title={plant.customer || ''}>{plant.customer}</td>
                                    <td className="text-wrap-site-plant" title={plant.site || ''}>{plant.site}</td>
                                    <td className="text-wrap-plants-plant" title={plant.plant || ''}>
                                        <button
                                            onClick={() => handlePlantClick(plant.id, plant.plant)}
                                            className="content-link" >
                                            {plant.plant}

                                        </button>
                                    </td>
                                    <td className={`content-status ${plant.status.toLowerCase() === 'active' ? 'status-active' : 'status-inactive'}`} title={plant.status || ''}>
                                        <span className='status-text'>{plant.status}</span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={4} className="table-empty">No plants found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                
            </div>
            <div className="pagination-fixed-container">
                <div className="plants-pagination">
                    <button
                        className="plants-pagination-prev"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1 || totalItems === 0}
                    >
                       <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginBottom:"-5px"}} >
               <path d="M15.8333 10L4.16668 10M4.16668 10L10 15.8334M4.16668 10L10 4.16669" stroke="#FCFCFD" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
               </svg>  Previous
                    </button>
                    <span className="plants-pagination-info">
                        Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {totalItems === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} plants
                    </span>
                    <button
                        className="plants-pagination-next"
                        onClick={handleNextPage}
                        disabled={totalItems === 0 || currentPage >= totalPages}
                    >
                        Next  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginBottom:"-5px"}} >
             <path d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333" stroke="#FCFCFD" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
             </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PlantDataTable;

declare global {
    interface Window {
        refreshPlantList?: () => void;
    }
}